// src/components/auction/AuctionTeamsTab.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AuctionTeamsTab.css';
import {
  ExternalLink, Trash2, X, Download, LayoutGrid, Brain, Plus,
  Star, Crown, Shield, Flame, Zap, Target, Trophy, Anchor,
  Gem, Rocket, Fish, Cat, Dog, Bird, Skull, Heart,
  Sparkles, Leaf, Compass, Music, FileSpreadsheet, AlertTriangle, RotateCcw,
  Search,
} from 'lucide-react';
import TeamBadge from '../TeamBadge';
import Tooltip from '../Tooltip';
import {
  ROLE_ORDER,
  ROLE_LABELS,
  AuctionTeamAvatar,
  fmt,
  getSmartSuggestions,
  v,
} from './auctionCommon';
import { HIDDEN_TEAM_NAME } from './auctionRosterUtils';
import {
  collectAmbiguousPlayers,
  buildLegheFantacalcioCsv,
  downloadTextFile,
  searchListone,
  loadOverrides,
  saveOverrides,
  clearOverrides,
  countOverrides,
  getListoneEntryById,
} from '../../utils/legheFantacalcioExport';

const MANTRA_BUCKETS = ['P', 'D', 'C', 'A'];
const CLASSIC_COMPACT_SECTIONS = ['P', 'D', 'C', 'A'];
const MANTRA_COMPACT_SECTIONS = ['P', 'M'];

const TEAM_ICON_CONFIGS = [
  { Icon: Star,     color: '#ef4444' }, //  0 rosso
  { Icon: Crown,    color: '#3b82f6' }, //  1 blu
  { Icon: Shield,   color: '#22c55e' }, //  2 verde
  { Icon: Flame,    color: '#f97316' }, //  3 arancione
  { Icon: Zap,      color: '#a855f7' }, //  4 viola
  { Icon: Target,   color: '#eab308' }, //  5 giallo
  { Icon: Trophy,   color: '#06b6d4' }, //  6 ciano
  { Icon: Anchor,   color: '#ec4899' }, //  7 rosa
  { Icon: Gem,      color: '#84cc16' }, //  8 lime
  { Icon: Rocket,   color: '#d946ef' }, //  9 fucsia
  { Icon: Fish,     color: '#f59e0b' }, // 10 ambra
  { Icon: Cat,      color: '#14b8a6' }, // 11 teal
  { Icon: Dog,      color: '#6366f1' }, // 12 indaco
  { Icon: Bird,     color: '#f43f5e' }, // 13 rosa-rosso
  { Icon: Skull,    color: '#10b981' }, // 14 smeraldo
  { Icon: Heart,    color: '#0ea5e9' }, // 15 cielo
  { Icon: Sparkles, color: '#8b5cf6' }, // 16 violetto
  { Icon: Leaf,     color: '#fb923c' }, // 17 albicocca
  { Icon: Compass,  color: '#4ade80' }, // 18 menta
  { Icon: Music,    color: '#c084fc' }, // 19 lavanda
];

function CompactTeamIcon({ teamIndex }) {
  const { Icon, color } = TEAM_ICON_CONFIGS[teamIndex % TEAM_ICON_CONFIGS.length];
  return (
    <div className="compact-team-icon" style={{ '--team-icon-color': color }}>
      <Icon />
    </div>
  );
}
const MANTRA_MOVEMENT_ORDER = ['Ds', 'Dc', 'Dd', 'B', 'M', 'C', 'E', 'W', 'T', 'A', 'Pc'];

function getRoleLabel(role) {
  const map = {
    P: 'Porta',
    D: 'Difesa',
    C: 'Centrocampo',
    A: 'Attacco',
    M: 'Movimento',
  };

  return map[role] || ROLE_LABELS[role] || role;
}

function splitRoleTokens(value = '') {
  return String(value || '')
    .split(/[/,|+]/g)
    .flatMap((part) => part.split(/\s+/g))
    .map((part) => part.trim())
    .filter(Boolean);
}

function getRoleText(purchase, rolesMode) {
  const roleValue = purchase?.playerSnapshot?.role || purchase?.role || '—';

  if (rolesMode !== 'mantra') {
    return String(roleValue || '—').trim() || '—';
  }

  if (Array.isArray(roleValue)) {
    return roleValue.map((r) => String(r).trim()).filter(Boolean).join('/');
  }

  const tokens = splitRoleTokens(roleValue);
  return tokens.length ? tokens.join('/') : String(roleValue || '—').trim() || '—';
}

function getMantraBucket(roleValue = '') {
  const tokens = splitRoleTokens(roleValue).map((t) => t.trim().toUpperCase());

  if (tokens.includes('P')) return 'P';
  if (tokens.some((t) => ['DD', 'DC', 'DS', 'B'].includes(t))) return 'D';
  if (tokens.some((t) => ['E', 'C', 'M', 'W', 'T'].includes(t))) return 'C';
  if (tokens.some((t) => ['A', 'PC'].includes(t))) return 'A';

  return 'A';
}

function RoleHeader({ role, compact = false, label }) {
  return (
    <div className={`role-title role-title--${role} ${compact ? 'role-title--compact' : ''}`}>
      <span className={`role-title__swatch role-title__swatch--${role}`} aria-hidden="true" />
      <div className="role-title__text">
        <strong>{label || getRoleLabel(role)}</strong>
      </div>
    </div>
  );
}

function MantraRoleChip({ role }) {
  const text = String(role || '—').trim() || '—';
  const bucket = getMantraBucket(text);

  return (
    <span
      className={`auction-role-chip auction-role-chip--mantra auction-role-chip--mantra-${bucket}`}
      aria-label={text}
    >
      {text}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeFilename(value = 'rose') {
  return (
    String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'rose'
  );
}

function getRoleIndex(role) {
  return ROLE_ORDER.indexOf(role);
}

function getDisplayRoleText(purchase, rolesMode) {
  const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';

  if (rolesMode !== 'mantra') {
    return String(roleValue || '').trim().toUpperCase() || 'A';
  }

  const tokens = splitRoleTokens(roleValue);
  return tokens.length ? tokens.join('/') : '—';
}

function buildExcelHtml({
  auctionName,
  teamNames,
  rosterByTeam,
  teamEconomy,
  rosterLimits,
  rolesMode,
}) {
  const createdAt = new Date().toLocaleString('it-IT');

  const summaryRows = [...teamNames]
    .map((team) => {
      const stats = teamEconomy?.[team] || {
        remaining: 0,
        spent: 0,
        totalPlayers: 0,
        spentByRole: { P: 0, D: 0, C: 0, A: 0 },
        purchasedByRole: { P: 0, D: 0, C: 0, A: 0 },
        remainingByRole: rosterLimits,
      };

      return `
        <tr>
          <td>${escapeHtml(team)}</td>
          <td style="text-align:center">${v(stats.totalPlayers)}</td>
          <td style="text-align:right">${v(stats.remaining)} cr</td>
          <td style="text-align:right">${v(stats.spent)} cr</td>
          <td style="text-align:right">${v(stats.remainingByRole?.P ?? rosterLimits.P)} cr</td>
          <td style="text-align:right">${v(stats.remainingByRole?.D ?? rosterLimits.D)} cr</td>
          <td style="text-align:right">${v(stats.remainingByRole?.C ?? rosterLimits.C)} cr</td>
          <td style="text-align:right">${v(stats.remainingByRole?.A ?? rosterLimits.A)} cr</td>
        </tr>
      `;
    })
    .join('');

  const teamSections = [...teamNames]
    .map((team, index) => {
      const roster = [...(rosterByTeam?.[team] || [])].sort((a, b) => {
        const ra = getRoleIndex(a?.playerSnapshot?.role || a?.role || 'A');
        const rb = getRoleIndex(b?.playerSnapshot?.role || b?.role || 'A');
        if (ra !== rb) return ra - rb;
        return (a.playerSnapshot?.name || a.playerName || '').localeCompare(
          b.playerSnapshot?.name || b.playerName || ''
        );
      });

      const rows = roster.length
        ? roster
          .map((p) => {
            const snap = p?.playerSnapshot || {};
            const prev = snap.previousSeasonStats || {};
            const roleText = getDisplayRoleText(p, rolesMode);

            return `
                <tr>
                  <td>${escapeHtml(roleText)}</td>
                  <td>${escapeHtml(snap.name || p.playerName || '')}</td>
                  <td>${escapeHtml(snap.team || p.playerTeam || '')}</td>
                  <td style="text-align:right">${v(p.credits)} cr</td>
                  <td>${escapeHtml(formatDate(p.acquiredAt))}</td>
                  <td style="text-align:right">${snap.caps ?? ''}</td>
                  <td style="text-align:right">${prev.caps ?? ''}</td>
                  <td style="text-align:right">${snap.markavg ?? ''}</td>
                  <td style="text-align:right">${snap.fmarkavg ?? ''}</td>
                  <td style="text-align:right">${snap.goals ?? ''}</td>
                  <td style="text-align:right">${snap.assists ?? ''}</td>
                </tr>
              `;
          })
          .join('')
        : `
          <tr>
            <td colspan="11">Nessun giocatore acquistato</td>
          </tr>
        `;

      return `
        <section class="sheet-section ${index > 0 ? 'page-break' : ''}">
          <h2>${escapeHtml(team)}</h2>
          <p>
            Giocatori acquistati: ${roster.length} • Crediti residui: ${v(teamEconomy?.[team]?.remaining ?? 0)} cr • Spesi: ${v(teamEconomy?.[team]?.spent ?? 0)} cr
          </p>

          <table>
            <thead>
              <tr>
                <th>Ruolo</th>
                <th>Giocatore</th>
                <th>Squadra</th>
                <th>Crediti</th>
                <th>Acquistato il</th>
                <th>PG att.</th>
                <th>PG prec.</th>
                <th>MV att.</th>
                <th>FV att.</th>
                <th>Gol</th>
                <th>Assist</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        </section>
      `;
    })
    .join('');

  return `
    <html>
      <head>
        <meta charset="UTF-8" />
        <style>
          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
            background: #fff;
            padding: 24px;
          }
          h1 {
            margin: 0 0 8px;
            font-size: 24px;
          }
          h2 {
            margin: 22px 0 6px;
            font-size: 18px;
          }
          p {
            margin: 0 0 14px;
            color: #4b5563;
            font-size: 12px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            margin-bottom: 18px;
          }
          th, td {
            border: 1px solid #d1d5db;
            padding: 6px 8px;
            font-size: 11px;
            vertical-align: middle;
            word-break: break-word;
          }
          th {
            background: #f3f4f6;
            text-align: left;
            font-weight: 700;
          }
          .page-break {
            page-break-before: always;
          }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(auctionName || 'Rose asta')}</h1>
        <p>Esportato il ${escapeHtml(createdAt)}</p>

        <h2>Riepilogo squadre</h2>
        <table>
          <thead>
            <tr>
              <th>Squadra</th>
              <th>Giocatori</th>
              <th>Crediti residui</th>
              <th>Spesi</th>
              <th>Portieri residui</th>
              <th>Difensori residui</th>
              <th>Centrocampisti residui</th>
              <th>Attaccanti residui</th>
            </tr>
          </thead>
          <tbody>
            ${summaryRows}
          </tbody>
        </table>

        ${teamSections}
      </body>
    </html>
  `;
}

function downloadExcelHtml(filename, html) {
  const blob = new Blob(['\ufeff', html], {
    type: 'application/vnd.ms-excel;charset=utf-8',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}

function getOverlaySuggestionRoles(teamStats) {
  return teamStats?.remainingByRole &&
    Object.prototype.hasOwnProperty.call(teamStats.remainingByRole, 'M')
    ? ['P', 'M']
    : ROLE_ORDER;
}

function getMantraSuggestionMovementIndex(purchase) {
  const roleText = getRoleText(purchase, 'mantra');
  const tokens = splitRoleTokens(roleText)
    .map((t) => String(t || '').trim())
    .filter(Boolean)
    .filter((t) => t !== 'P');

  const indexes = tokens
    .map((token) => MANTRA_MOVEMENT_ORDER.indexOf(token))
    .filter((idx) => idx >= 0);

  return indexes.length ? Math.min(...indexes) : MANTRA_MOVEMENT_ORDER.length;
}

function sortMantraSuggestionPlayers(list) {
  return [...list].sort((a, b) => {
    const ia = getMantraSuggestionMovementIndex(a);
    const ib = getMantraSuggestionMovementIndex(b);
    if (ia !== ib) return ia - ib;

    if (b.currentCaps !== a.currentCaps) return b.currentCaps - a.currentCaps;
    if (b.previousCaps !== a.previousCaps) return b.previousCaps - a.previousCaps;
    if (b.value !== a.value) return b.value - a.value;
    if (a.estimatedPrice !== b.estimatedPrice) return a.estimatedPrice - b.estimatedPrice;

    return String(a.name || '').localeCompare(String(b.name || ''), 'it');
  });
}

function AISuggestionsOverlay({
  players,
  purchases,
  myRosterPurchases,
  auction,
  teamStats,
  totalTeams,
  teamName,
  onClose,
  rolesMode,
  onPurchase,
  readOnly,
  playProbabilityById,
}) {
  const [addingId, setAddingId] = useState(null);

  // Aggiunge il giocatore suggerito alla squadra selezionata,
  // usando la quota (valore) come crediti d'acquisto
  const handleAddSuggestion = async (suggestion) => {
    if (!onPurchase || readOnly || addingId) return;

    const player = (players || []).find((pl) => String(pl.id) === String(suggestion.id));
    if (!player) return;

    const credits = Math.max(1, Math.round(v(suggestion.value ?? player.value)));

    setAddingId(String(suggestion.id));
    try {
      await onPurchase({ player, teamName, credits });
    } finally {
      setAddingId(null);
    }
  };

  const suggestions = useMemo(() => {
    return getSmartSuggestions({
      players,
      purchases,
      myRosterPurchases,
      auction,
      teamStats,
      totalTeams,
      playProbabilityById,
    });
  }, [players, purchases, myRosterPurchases, auction, teamStats, totalTeams, playProbabilityById]);

  const suggestionRoles = useMemo(
    () => getOverlaySuggestionRoles(teamStats),
    [teamStats]
  );

  const isMantra = suggestionRoles.includes('M');

  const hasAnySuggestion = suggestionRoles.some(
    (role) => (suggestions[role] || []).length > 0
  );

  const freeSlots = suggestionRoles.reduce(
    (acc, role) => acc + (teamStats?.remainingByRole?.[role] || 0),
    0
  );

  // In mantra i suggerimenti sono un piano d'acquisto: mostra il costo totale
  const planCost = isMantra
    ? suggestionRoles.reduce(
      (acc, role) => acc + (suggestions[role] || []).reduce((s, p) => s + v(p.estimatedPrice), 0),
      0
    )
    : null;

  // Raggruppa i giocatori già acquistati per ruolo (stesso bucketing dell'overlay)
  const ownedByRole = useMemo(() => {
    const grouped = {};
    suggestionRoles.forEach((role) => { grouped[role] = []; });

    (myRosterPurchases || [])
      .filter((p) => !p?.deleted)
      .forEach((purchase) => {
        const roleValue = purchase?.playerSnapshot?.role || purchase?.role || '';

        let bucket;
        if (isMantra) {
          const tokens = splitRoleTokens(roleValue)
            .map((t) => String(t || '').trim().toUpperCase())
            .filter(Boolean);
          if (tokens.includes('P')) bucket = 'P';
          else bucket = 'M';
        } else {
          const upper = String(roleValue).trim().toUpperCase();
          if (upper === 'P') bucket = 'P';
          else if (['D', 'DD', 'DC', 'DS', 'B'].includes(upper)) bucket = 'D';
          else if (['C', 'E', 'M', 'W', 'T'].includes(upper)) bucket = 'C';
          else bucket = 'A';
        }

        if (grouped[bucket]) grouped[bucket].push(purchase);
      });

    // Movimento mantra in ordine di ruolo (Ds, Dc, Dd, B, M, C, E, W, T, A, Pc),
    // altrimenti alfabetico per nome
    suggestionRoles.forEach((role) => {
      grouped[role].sort((a, b) => {
        if (isMantra && role === 'M') {
          const ia = getMantraSuggestionMovementIndex(a);
          const ib = getMantraSuggestionMovementIndex(b);
          if (ia !== ib) return ia - ib;
        }
        return (a.playerSnapshot?.name || a.playerName || '').localeCompare(
          b.playerSnapshot?.name || b.playerName || '', 'it'
        );
      });
    });

    return grouped;
  }, [myRosterPurchases, suggestionRoles, isMantra]);

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <AuctionTeamAvatar name={teamName || 'AI'} />
            <div>
              <span>Consigli AI{teamName ? ` · ${teamName}` : ''}</span>
              <h3>Squadra con suggerimenti AI</h3>
            </div>
          </div>

          <button className="overlay-close" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="overlay-team-stats">
          <div>
            <span>Crediti residui</span>
            <strong>{teamStats?.remaining ?? 0} cr</strong>
          </div>
          <div>
            <span>Giocatori già acquistati</span>
            <strong>{myRosterPurchases?.filter((p) => !p?.deleted).length ?? 0}</strong>
          </div>
          <div>
            <span>Slot ancora liberi</span>
            <strong>{freeSlots}</strong>
          </div>
          {isMantra && planCost !== null && (
            <div>
              <span>Costo piano suggerito</span>
              <strong>{planCost} cr</strong>
            </div>
          )}
        </div>

        <div className="ai-role-section">
          {suggestionRoles.map((role) => {
            const suggestedList = suggestions[role] || [];
            const ownedList     = ownedByRole[role]  || [];

            // Nascondi la sezione solo se non c'è nulla né di suggerito né di acquistato
            if (!suggestedList.length && !ownedList.length) return null;

            const displaySuggested =
              isMantra && role === 'M'
                ? sortMantraSuggestionPlayers(suggestedList)
                : [...suggestedList];

            return (
              <section key={role} className="overlay-role-section">
                <div className="overlay-role-section__head">
                  <RoleHeader role={role} compact />
                  <div className="overlay-role-section__meta">
                    <strong>{displaySuggested.length}</strong>
                    <span>candidati</span>
                  </div>
                </div>

                {/* ── Giocatori già acquistati ── */}
                {ownedList.length > 0 && (
                  <div className="ai-owned-block">
                    <div className="ai-owned-block__label">
                      <span className="ai-section-badge ai-section-badge--owned">
                        ✓ In rosa
                      </span>
                      <span className="ai-owned-block__count">
                        {ownedList.length} acquistati
                      </span>
                    </div>

                    <div className="ai-player-list">
                      {ownedList.map((purchase) => {
                        const snap = purchase?.playerSnapshot || {};
                        return (
                          <div key={purchase.id} className="ai-player ai-player--owned">
                            <TeamBadge team={snap.team || purchase?.playerTeam || '—'} size={30} />

                            <div className="ai-player__body">
                              <div className="ai-player__nameRow">
                                <strong>{snap.name || purchase?.playerName || '—'}</strong>

                                {isMantra && (
                                  <div className="ai-player__roles">
                                    {splitRoleTokens(String(snap.role || purchase?.role || ''))
                                      .filter(Boolean)
                                      .map((r) => (
                                        <MantraRoleChip key={`${purchase.id}-${r}`} role={r} />
                                      ))}
                                  </div>
                                )}

                                <span className="ai-owned-price">{purchase.credits} cr</span>
                              </div>

                              <span>
                                PG att {snap.caps ?? '—'} • PG prec {snap.previousSeasonStats?.caps ?? '—'} • MV {fmt(snap.markavg)} • FV {fmt(snap.fmarkavg)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Giocatori suggeriti da AI ── */}
                {displaySuggested.length > 0 && (
                  <div className="ai-suggested-block">
                    {ownedList.length > 0 && (
                      <div className="ai-suggested-block__label">
                        <span className="ai-section-badge ai-section-badge--ai">
                          🤖 Suggeriti AI
                        </span>
                        <span className="ai-owned-block__count">
                          {displaySuggested.length} candidati
                        </span>
                      </div>
                    )}

                    <div className="ai-player-list">
                      {displaySuggested.map((p) => (
                        <div key={p.id} className="ai-player">
                          <TeamBadge team={p.team} size={30} />

                          <div className="ai-player__body">
                            <div className="ai-player__nameRow">
                              <strong>{p.name}</strong>

                              {isMantra && (
                                <div className="ai-player__roles">
                                  {splitRoleTokens(String(p.role || ''))
                                    .filter(Boolean)
                                    .map((r) => (
                                      <MantraRoleChip key={`${p.id}-${r}`} role={r} />
                                    ))}
                                </div>
                              )}
                            </div>

                            <span>
                              {p.isPlaceholder ? (
                                <>Riserva della squadra del portiere titolare • 1 cr</>
                              ) : (
                                <>
                                  Att {p.currentCaps || 0} • Prec {p.previousCaps || 0} • Val {fmt(p.value)} • FV{' '}
                                  {fmt(p.fmarkavg)}
                                  {!isMantra && <> • ~{p.estimatedPrice} cr</>}
                                </>
                              )}
                            </span>
                          </div>

                          <div className="ai-player__aside">
                            {isMantra && !p.isPlaceholder && (
                              <span className="ai-player__price">{p.estimatedPrice} cr</span>
                            )}

                            {!readOnly && onPurchase && !p.isPlaceholder && (
                              <Tooltip
                                as="button"
                                className="overlay-player-row__actionBtn ai-add-btn"
                                type="button"
                                disabled={addingId !== null}
                                onClick={() => handleAddSuggestion(p)}
                                content={`Aggiungi a ${teamName} per ${Math.max(1, Math.round(v(p.value)))} cr`}
                              >
                                <Plus size={14} />
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!displaySuggested.length && ownedList.length > 0 && (
                  <div className="overlay-empty overlay-empty--small">
                    Nessun altro candidato disponibile per questo ruolo.
                  </div>
                )}
              </section>
            );
          })}

          {!hasAnySuggestion && myRosterPurchases?.filter((p) => !p?.deleted).length === 0 && (
            <div className="overlay-empty">
              Nessun suggerimento disponibile. Probabilmente la rosa è già completa o il budget è troppo basso.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getCompactRoleBucket(purchase, rolesMode) {
  const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';

  if (rolesMode === 'mantra') {
    const roleText = getRoleText(purchase, rolesMode);
    const tokens = splitRoleTokens(roleText).map((t) => String(t || '').trim()).filter(Boolean);
    return tokens.includes('P') ? 'P' : 'M';
  }

  const upper = String(roleValue || 'A').trim().toUpperCase();
  if (upper === 'P') return 'P';
  if (['D', 'DD', 'DC', 'DS', 'B'].includes(upper)) return 'D';
  if (['C', 'E', 'M', 'W', 'T'].includes(upper)) return 'C';
  return 'A';
}

function getMantraMovementSortIndex(purchase) {
  const roleText = getRoleText(purchase, 'mantra');
  const tokens = splitRoleTokens(roleText)
    .map((t) => String(t || '').trim())
    .filter(Boolean)
    .filter((t) => t !== 'P');

  const indexes = tokens
    .map((token) => MANTRA_MOVEMENT_ORDER.indexOf(token))
    .filter((idx) => idx >= 0);

  return indexes.length ? Math.min(...indexes) : MANTRA_MOVEMENT_ORDER.length;
}

function sortCompactPlayers(list, rolesMode, bucket) {
  return [...list].sort((a, b) => {
    if (rolesMode === 'mantra' && bucket === 'M') {
      const ia = getMantraMovementSortIndex(a);
      const ib = getMantraMovementSortIndex(b);
      if (ia !== ib) return ia - ib;
    }

    const dateA = new Date(a.acquiredAt || 0).getTime();
    const dateB = new Date(b.acquiredAt || 0).getTime();
    if (dateA !== dateB) return dateA - dateB;

    return String(a.playerSnapshot?.name || a.playerName || '').localeCompare(
      String(b.playerSnapshot?.name || b.playerName || '')
    );
  });
}

function buildCompactViewModel({ teamNames, rosterByTeam, rolesMode }) {
  const sections = rolesMode === 'mantra' ? MANTRA_COMPACT_SECTIONS : CLASSIC_COMPACT_SECTIONS;

  return (teamNames || []).map((team) => {
    const roster = [...(rosterByTeam?.[team] || [])].filter((p) => !p.deleted);

    const grouped = {};
    sections.forEach((key) => {
      grouped[key] = [];
    });

    roster.forEach((purchase) => {
      const bucket = getCompactRoleBucket(purchase, rolesMode);
      if (!grouped[bucket]) grouped[bucket] = [];
      grouped[bucket].push(purchase);
    });

    sections.forEach((key) => {
      grouped[key] = sortCompactPlayers(grouped[key] || [], rolesMode, key);
    });

    return {
      team,
      roster,
      sections,
      grouped,
    };
  });
}

const COMPACT_NAME_MAX = 11;
// Numero di colonne oltre il quale lo spazio per colonna diventa stretto e
// conviene troncare i nomi lunghi. Sotto (es. leghe a 2-5 squadre) c'è spazio.
const COMPACT_TRUNCATE_MIN_COLS = 6;
function truncateCompactName(name) {
  if (!name || name.length <= COMPACT_NAME_MAX) return name;
  return name.substring(0, 9).trimEnd() + '..';
}

function CompactPlayerRow({ purchase, isNew, truncateName }) {
  const snap = purchase?.playerSnapshot || {};
  const fullName = snap.name || purchase?.playerName || '—';

  return (
    <div className={`compact-player-row${isNew ? ' compact-player-row--new' : ''}`}>
      <TeamBadge team={snap.team || purchase?.playerTeam || '—'} size={28} />
      <div className="compact-player-row__body">
        <Tooltip as="strong" content={fullName}>
          {truncateName ? truncateCompactName(fullName) : fullName}
        </Tooltip>
      </div>
      <div className="compact-player-row__price">{v(purchase?.credits)}</div>
    </div>
  );
}

function CompactTeamColumn({ team, teamIndex, roster, grouped, stats, rolesMode, activeRoles, highlightedIds, isHighlighted, truncateName }) {
  const allSections = rolesMode === 'mantra' ? MANTRA_COMPACT_SECTIONS : CLASSIC_COMPACT_SECTIONS;
  const sections = activeRoles ? allSections.filter((s) => activeRoles.has(s)) : allSections;

  return (
    <article className={`compact-team-column compact-team-column--${rolesMode}${isHighlighted ? ' compact-team-column--new' : ''}`}>
      <div className="compact-team-column__head">
        <div className="compact-team-column__title">
          <CompactTeamIcon teamIndex={teamIndex} />
          <div className="compact-team-column__nameblock">
            <Tooltip as="h3" content={team}>{team}</Tooltip>
            <span>{roster.length} giocatori</span>
          </div>
        </div>

        <div className="compact-team-column__mini">
          <strong>{v(stats?.remaining ?? 0)} cr</strong>
        </div>
      </div>

      <div className="compact-team-column__sections">
        {sections.map((section) => {
          const list = grouped?.[section] || [];
          const label =
            rolesMode === 'mantra'
              ? section === 'P'
                ? 'Portieri'
                : 'Movimento'
              : getRoleLabel(section);

          return (
            <section key={section} className={`compact-role-block compact-role-block--${section}`}>
              <div className="compact-role-block__head">
                <RoleHeader
                  role={section}
                  compact
                  label={rolesMode === 'mantra' && section === 'M' ? 'Movimento' : label}
                />
                <span className="compact-role-block__count">{list.length}</span>
              </div>

              <div className="compact-role-list">
                {list.length ? (
                  list.map((purchase) => <CompactPlayerRow key={purchase.id} purchase={purchase} isNew={highlightedIds?.has(purchase.id)} truncateName={truncateName} />)
                ) : (
                  <div className="compact-role-empty">—</div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </article>
  );
}

// Riquadro "in evidenza": il giocatore attualmente selezionato nel tab
// Mercato + i crediti impostati lì. Vive sulla STESSA riga dell'intestazione
// ("Riassunto squadre"), quindi è pensato per stare su una sola riga e
// restare dentro l'altezza dell'header. Il tab Mercato resta montato quando
// non è quello visibile, quindi questo dato è live anche qui.
function MarketSpotlight({ selection, rolesMode }) {
  if (!selection?.player) return null;

  const { player, credits } = selection;
  const bucket = getCompactRoleBucket(player, rolesMode);
  const roleText = getDisplayRoleText(player, rolesMode);

  return (
    <div className="compact-spotlight">
      <span className="compact-spotlight__eyebrow">Asta per</span>
      <TeamBadge team={player.team} size={48} />
      <span className={`compact-spotlight__role compact-spotlight__role--${bucket}`}>{roleText}</span>
      <span className="compact-spotlight__name">{player.name}</span>
      <div className="compact-spotlight__credits">
        <strong>{credits || 0}</strong>
        <span>cr</span>
      </div>
    </div>
  );
}

function CompactViewOverlay({ teamNames, rosterByTeam, teamEconomy, rolesMode, onClose, marketSelection }) {
  const allSections = rolesMode === 'mantra' ? MANTRA_COMPACT_SECTIONS : CLASSIC_COMPACT_SECTIONS;
  const [activeRoles, setActiveRoles] = useState(() => new Set(allSections));

  const toggleRole = (role) => {
    setActiveRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        if (next.size > 1) next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  };

  const [selectedTeams, setSelectedTeams] = useState(() => new Set());

  const toggleTeam = (team) => {
    setSelectedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(team)) next.delete(team); else next.add(team);
      if (next.size === teamNames.length) return new Set();
      return next;
    });
  };

  // Animazione "nuovo arrivo": quando appare un acquisto non ancora visto, la
  // riga del giocatore lampeggia per un istante e — novità — anche l'intera
  // colonna della squadra che l'ha preso, così si nota a colpo d'occhio anche
  // se si sta guardando un'altra colonna. Vale per chiunque tenga aperta la
  // visualizzazione compatta (proprietario incluso: prima scattava solo per
  // chi guarda in sola lettura), perché il tab Mercato resta montato in
  // background e l'acquisto può arrivare mentre si è su questa vista.
  const knownIdsRef = useRef(null);
  const [highlightedIds, setHighlightedIds] = useState(() => new Set());
  const [highlightedTeams, setHighlightedTeams] = useState(() => new Set());

  useEffect(() => {
    const ids = new Set();
    Object.values(rosterByTeam || {}).forEach((roster) => {
      roster.forEach((p) => { if (p.id) ids.add(p.id); });
    });
    knownIdsRef.current = ids;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!knownIdsRef.current) return;
    const newIds = [];
    const newTeams = new Set();
    Object.entries(rosterByTeam || {}).forEach(([team, roster]) => {
      roster.forEach((p) => {
        if (p.id && !knownIdsRef.current.has(p.id)) {
          newIds.push(p.id);
          newTeams.add(team);
          knownIdsRef.current.add(p.id);
        }
      });
    });
    if (!newIds.length) return;
    setHighlightedIds((prev) => new Set([...prev, ...newIds]));
    setHighlightedTeams((prev) => new Set([...prev, ...newTeams]));
    const timer = setTimeout(() => {
      setHighlightedIds((prev) => {
        const next = new Set(prev);
        newIds.forEach((id) => next.delete(id));
        return next;
      });
      setHighlightedTeams((prev) => {
        const next = new Set(prev);
        newTeams.forEach((team) => next.delete(team));
        return next;
      });
    }, 2500);
    return () => clearTimeout(timer);
  }, [rosterByTeam]);

  const compactTeams = useMemo(
    () => buildCompactViewModel({ teamNames, rosterByTeam, rolesMode }),
    [teamNames, rosterByTeam, rolesMode]
  );

  const isFiltered = selectedTeams.size > 0;
  const visibleTeams = isFiltered
    ? compactTeams.filter((td) => selectedTeams.has(td.team))
    : compactTeams;

  const totalPlayers = compactTeams.reduce((acc, item) => acc + (item.roster?.length || 0), 0);
  const colCount = isFiltered ? selectedTeams.size : Math.max(teamNames.length, 1);
  // Tronca i nomi solo quando le colonne sono tante (poco spazio per colonna):
  // con poche squadre a schermo il nome intero ci sta comodamente.
  const shouldTruncateNames = colCount >= COMPACT_TRUNCATE_MIN_COLS;

  const rolePillLabel = {
    P: 'Portieri',
    D: 'Difensori',
    C: 'Centrocampisti',
    A: 'Attaccanti',
    M: 'Movimento',
  };

  return (
    <div className="compact-overlay-backdrop" onClick={onClose}>
      <div className="compact-overlay-panel glass" onClick={(e) => e.stopPropagation()}>
        <div className="compact-overlay-head">
          <div className="overlay-head__title">
            <AuctionTeamAvatar name="CM" />
            <div>
              <span>Visualizzazione compatta</span>
              <h3>Riassunto squadre</h3>
              <small>{totalPlayers} giocatori totali</small>
            </div>
          </div>

          <MarketSpotlight selection={marketSelection} rolesMode={rolesMode} />

          <button className="overlay-close" type="button" onClick={onClose} aria-label="Chiudi overlay">
            <X size={18} />
          </button>
        </div>

        <div className="compact-overlay-filters">
          {allSections.map((role) => (
            <button
              key={role}
              type="button"
              className={`compact-filter-pill compact-filter-pill--${role} ${activeRoles.has(role) ? 'is-active' : ''}`}
              onClick={() => toggleRole(role)}
            >
              <span className="compact-filter-pill__swatch" />
              {rolePillLabel[role]}
            </button>
          ))}

          <span className="compact-filters-sep" />

          <button
            type="button"
            className={`compact-filter-pill compact-filter-pill--team ${!isFiltered ? 'is-active' : ''}`}
            onClick={() => setSelectedTeams(new Set())}
          >
            Tutte
          </button>
          {compactTeams.map((td, idx) => (
            <button
              key={td.team}
              type="button"
              className={`compact-filter-pill compact-filter-pill--team ${selectedTeams.has(td.team) ? 'is-active' : ''}`}
              style={{ '--team-color': TEAM_ICON_CONFIGS[idx % TEAM_ICON_CONFIGS.length].color }}
              onClick={() => toggleTeam(td.team)}
            >
              {td.team}
            </button>
          ))}
        </div>

        <div className="compact-overlay-scroll">
          <div className={isFiltered ? 'compact-overlay-center' : ''}>
          <div
            className={`compact-overlay-grid compact-overlay-grid--${rolesMode}${!isFiltered ? ' compact-overlay-grid--all' : ''}`}
            style={{
              gridTemplateColumns: isFiltered
                ? `repeat(${colCount}, clamp(200px, calc(80vw / ${colCount}), 420px))`
                : `repeat(${colCount}, minmax(0, 1fr))`,
            }}
          >
            {visibleTeams.map((teamData) => {
              const originalIdx = compactTeams.findIndex((td) => td.team === teamData.team);
              return (
                <CompactTeamColumn
                  key={teamData.team}
                  team={teamData.team}
                  teamIndex={originalIdx}
                  roster={teamData.roster}
                  grouped={teamData.grouped}
                  stats={teamEconomy?.[teamData.team]}
                  rolesMode={rolesMode}
                  activeRoles={activeRoles}
                  highlightedIds={highlightedIds}
                  isHighlighted={highlightedTeams.has(teamData.team)}
                  truncateName={shouldTruncateNames}
                />
              );
            })}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegheResolutionRow({ item, choice, onChoose }) {
  const [editing, setEditing] = useState(choice === undefined);
  const [query, setQuery] = useState('');

  const handlePick = (id) => {
    onChoose(id);
    setEditing(false);
  };

  if (!editing) {
    const resolvedEntry = typeof choice === 'number' ? getListoneEntryById(choice) : null;
    const isChanged = item.resolvedId !== undefined && choice !== item.resolvedId;

    return (
      <div className={`leghe-resolve-row leghe-resolve-row--compact${isChanged ? ' leghe-resolve-row--changed' : ''}`}>
        <div className="leghe-resolve-row__summary">
          <span className="leghe-resolve-row__from">
            <strong>{item.name}</strong>
            <small>{item.team}</small>
          </span>
          <span className="leghe-resolve-row__arrow">→</span>
          {choice === null ? (
            <span className="leghe-resolve-row__to leghe-resolve-row__to--skip">Escluso dall'esportazione</span>
          ) : resolvedEntry ? (
            <span className="leghe-resolve-row__to">
              {resolvedEntry.name} <small>{resolvedEntry.team}</small>
            </span>
          ) : (
            <span className="leghe-resolve-row__to leghe-resolve-row__to--missing">ID {choice} non trovato</span>
          )}
        </div>

        <button type="button" className="leghe-resolve-edit" onClick={() => setEditing(true)}>
          Modifica
        </button>
      </div>
    );
  }

  const results = query.trim()
    ? searchListone(query, 20)
    : (item.candidates.length ? item.candidates : searchListone(item.name, 8));

  return (
    <div className="leghe-resolve-row">
      <div className="leghe-resolve-row__head">
        <div>
          <strong>{item.name}</strong>
          <span>{item.team}</span>
        </div>
        <span className="leghe-resolve-row__owner">{item.managerTeams.join(', ')}</span>
      </div>

      <input
        type="text"
        className="leghe-resolve-row__search"
        placeholder="Cerca nel listone delle leghe..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="leghe-resolve-row__options">
        {results.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`leghe-resolve-chip ${choice === c.id ? 'is-active' : ''}`}
            onClick={() => handlePick(c.id)}
          >
            {c.name} <small>{c.team}</small>
          </button>
        ))}
        {!results.length && <span className="leghe-resolve-row__empty">Nessun risultato</span>}
      </div>

      <button
        type="button"
        className={`leghe-resolve-skip ${choice === null ? 'is-active' : ''}`}
        onClick={() => handlePick(null)}
      >
        <X size={13} />
        Escludi dall'esportazione
      </button>
    </div>
  );
}

function LegheResolutionOverlay({ items, onCancel, onConfirm, onReset }) {
  const [choices, setChoices] = useState(() => {
    const initial = {};
    items.forEach((it) => {
      if (it.resolvedId !== undefined) initial[it.key] = it.resolvedId;
    });
    return initial;
  });

  const resolvedCount = items.filter((it) => Object.prototype.hasOwnProperty.call(choices, it.key)).length;
  const allResolved = resolvedCount === items.length;

  return (
    <div className="overlay-backdrop" onClick={onCancel}>
      <div className="overlay-panel glass leghe-resolve-panel" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <AlertTriangle size={22} color="#f59e0b" />
            <div>
              <span>Esporta per Leghe</span>
              <h3>Conferma {items.length} giocatori incerti</h3>
            </div>
          </div>

          <button className="overlay-close" type="button" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <p className="leghe-unmatched-hint">
          Questi giocatori non hanno una corrispondenza automatica univoca nel listone.
          Scegli quello giusto, oppure escludili dall'esportazione se non
          sono quotati: la scelta resta salvata per i prossimi export.
        </p>

        <div className="leghe-resolve-list">
          {items.map((item) => (
            <LegheResolutionRow
              key={item.key}
              item={item}
              choice={choices[item.key]}
              onChoose={(id) => setChoices((prev) => ({ ...prev, [item.key]: id }))}
            />
          ))}
        </div>

        <div className="leghe-resolve-footer">
          <span>{resolvedCount}/{items.length} risolti</span>
          <div className="leghe-resolve-footer__actions">
            <button type="button" className="leghe-resolve-reset" onClick={onReset}>
              <RotateCcw size={13} />
              Cancella scelte salvate
            </button>
            <button
              type="button"
              className="leghe-resolve-confirm"
              disabled={!allResolved}
              onClick={() => onConfirm(choices)}
            >
              Conferma ed esporta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

// Con un solo partecipante, il pulsante "Rimuovi" in AuctionMarketTab assegna
// il giocatore alla squadra fittizia HIDDEN_TEAM_NAME invece di eliminarlo
// davvero (serve a toglierlo dagli acquistabili senza assegnarlo a nessuno).
// Questo overlay è la "via di recupero" in caso di errore: cerca tra questi
// giocatori e rimuove del tutto l'acquisto, facendolo tornare disponibile.
// A differenza della rimozione di un giocatore da una squadra reale, qui non
// serve una conferma aggiuntiva: il giocatore non è mai stato assegnato a
// nessuno, quindi "Rendi acquistabile" agisce subito.
function RemovedPlayersOverlay({ purchases, onRestorePurchase, onClose, readOnly }) {
  const [search, setSearch] = useState('');

  const removedPurchases = useMemo(() => {
    return (purchases || [])
      .filter((p) => !p?.deleted && p.teamName === HIDDEN_TEAM_NAME)
      .sort((a, b) => {
        const nameA = a.playerSnapshot?.name || a.playerName || '';
        const nameB = b.playerSnapshot?.name || b.playerName || '';
        return nameA.localeCompare(nameB, 'it');
      });
  }, [purchases]);

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return removedPurchases;
    return removedPurchases.filter((p) =>
      normalizeText(p.playerSnapshot?.name || p.playerName || '').includes(q)
    );
  }, [removedPurchases, search]);

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <div>
              <span>Recupero errori</span>
              <h3>Giocatori rimossi</h3>
            </div>
          </div>

          <button className="overlay-close" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <label className="market-search">
          <Search size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca giocatore rimosso"
            autoFocus
          />
        </label>

        {filtered.length === 0 ? (
          <div className="overlay-empty">
            {removedPurchases.length === 0
              ? 'Nessun giocatore rimosso al momento.'
              : 'Nessun giocatore corrisponde alla ricerca.'}
          </div>
        ) : (
          <div className="ai-player-list">
            {filtered.map((purchase) => {
              const snap = purchase?.playerSnapshot || {};
              return (
                <div key={purchase.playerId} className="ai-player">
                  <TeamBadge team={snap.team || purchase?.playerTeam || '—'} size={30} />

                  <div className="ai-player__body">
                    <div className="ai-player__nameRow">
                      <strong>{snap.name || purchase?.playerName || '—'}</strong>
                    </div>
                    <span>{snap.team || purchase?.playerTeam || '—'}</span>
                  </div>

                  {!readOnly && (
                    <Tooltip
                      as="button"
                      type="button"
                      className="removed-player-restore-btn"
                      onClick={() => onRestorePurchase(purchase.playerId)}
                      content="Rimuove l'acquisto e riporta il giocatore tra gli acquistabili"
                    >
                      <RotateCcw size={14} />
                      Rendi acquistabile
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AuctionTeamsTab({
  auction,
  teamNames,
  rosterByTeam,
  teamEconomy,
  rosterLimits,
  players,
  myTeamName,
  myRosterPurchases,
  allPurchases,
  rolesMode = 'classic',
  onRemovePurchase,
  onRestorePurchase,
  onPurchase,
  onOpenPlayerDetail,
  readOnly = false,
  playProbabilityById = {},
  marketSelection = null,
}) {
  const [aiOverlayTeam, setAiOverlayTeam] = useState(null);
  const [compactOverlayOpen, setCompactOverlayOpen] = useState(false);
  const [legheUnmatched, setLegheUnmatched] = useState(null);
  const [legheResolution, setLegheResolution] = useState(null);
  const [removedOverlayOpen, setRemovedOverlayOpen] = useState(false);

  const removedPurchasesCount = useMemo(
    () => (allPurchases || []).filter((p) => !p?.deleted && p.teamName === HIDDEN_TEAM_NAME).length,
    [allPurchases]
  );

  useEffect(() => {
    const shouldLock = aiOverlayTeam !== null || compactOverlayOpen || legheResolution !== null || removedOverlayOpen;
    document.body.style.overflow = shouldLock ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [aiOverlayTeam, compactOverlayOpen, legheResolution, removedOverlayOpen]);

  const handleExport = () => {
    const html = buildExcelHtml({
      auctionName: auction?.name || 'Rose asta',
      teamNames,
      rosterByTeam,
      teamEconomy,
      rosterLimits,
      rolesMode,
    });

    const filename = `${normalizeFilename(auction?.name || 'rose-asta')}.xls`;
    downloadExcelHtml(filename, html);
  };

  const runLegheFantacalcioExport = () => {
    const { csv, unmatched } = buildLegheFantacalcioCsv({ teamNames, rosterByTeam });
    const filename = `${normalizeFilename(auction?.name || 'rose-asta')}-leghe-fantacalcio.csv`;
    downloadTextFile(filename, csv);
    setLegheUnmatched(unmatched.length ? unmatched : null);
  };

  const handleExportLegheFantacalcio = () => {
    const ambiguous = collectAmbiguousPlayers({ teamNames, rosterByTeam });
    if (ambiguous.length) {
      setLegheResolution({ items: ambiguous, resetKey: 0 });
    } else {
      runLegheFantacalcioExport();
    }
  };

  const handleConfirmLegheResolution = (choices) => {
    const overrides = loadOverrides();
    Object.entries(choices).forEach(([key, idOrNull]) => {
      overrides[key] = idOrNull;
    });
    saveOverrides(overrides);
    setLegheResolution(null);
    runLegheFantacalcioExport();
  };

  const handleResetLegheOverrides = () => {
    const count = countOverrides();
    if (!count) {
      window.alert('Nessuna scelta salvata da azzerare per l\'export Leghe Fantacalcio.');
      return;
    }
    const ok = window.confirm(
      `Cancellare le ${count} scelte salvate per i giocatori incerti dell'export Leghe Fantacalcio? ` +
      'Dovrai sceglierle di nuovo.'
    );
    if (!ok) return;

    clearOverrides();
    setLegheResolution((prev) => {
      if (!prev) return prev;
      const refreshed = collectAmbiguousPlayers({ teamNames, rosterByTeam });
      return { items: refreshed, resetKey: (prev.resetKey || 0) + 1 };
    });
  };

  return (
    <section className="teams-clean-grid">
      <div className="teams-clean-grid__toolbar">
        <div className="teams-clean-grid__toolbarText">
          <h2>Riepilogo squadre</h2>
          <span>Visualizza tutte le rose.</span>
        </div>

        <div className="teams-toolbar-group">
          <button
            className="teams-toolbar-group__btn teams-toolbar-group__btn--compact"
            type="button"
            onClick={() => setCompactOverlayOpen(true)}
          >
            <LayoutGrid size={16} />
            Visualizzazione compatta
          </button>

          <button className="teams-toolbar-group__btn" type="button" onClick={handleExportLegheFantacalcio}>
            <FileSpreadsheet size={16} />
            Esporta per Leghe
          </button>

          <button className="teams-toolbar-group__btn" type="button" onClick={handleExport}>
            <Download size={16} />
            Esporta in Excel
          </button>

          {teamNames.length === 1 && (
            <Tooltip
              as="button"
              className="teams-toolbar-group__btn"
              type="button"
              onClick={() => setRemovedOverlayOpen(true)}
              content="Recupera giocatori rimossi per errore (squadra fittizia usata con un solo partecipante)"
            >
              <RotateCcw size={16} />
              Giocatori rimossi
              {removedPurchasesCount > 0 && (
                <span className="teams-toolbar-group__badge">{removedPurchasesCount}</span>
              )}
            </Tooltip>
          )}
        </div>
      </div>

      {teamNames.map((team) => {
        const roster = rosterByTeam[team] || [];
        const stats = teamEconomy[team] || {
          remaining: auction?.credits || 0,
          spent: 0,
          totalPlayers: 0,
          spentByRole: { P: 0, D: 0, C: 0, A: 0 },
          purchasedByRole: { P: 0, D: 0, C: 0, A: 0 },
          remainingByRole: rosterLimits,
        };

        const usedPercent = auction?.credits
          ? Math.min(((auction.credits - stats.remaining) / auction.credits) * 100, 100)
          : 0;

        const buckets = rolesMode === 'mantra' ? MANTRA_BUCKETS : ROLE_ORDER;

        return (
          <article className="team-card-clean" key={team}>
            <div className="team-card-clean__header">
              <div className="team-card-clean__title">
                <h2>{team}</h2>
                <span>{roster.length} giocatori</span>
              </div>

              <div className="team-card-clean__credits">
                <div>
                  <span>Residui</span>
                  <strong>{stats.remaining} cr</strong>
                </div>
                <div>
                  <span>Spesi</span>
                  <strong>{stats.spent} cr</strong>
                </div>
              </div>

              <button
                className="ai-suggest-btn"
                onClick={() => setAiOverlayTeam(team)}
                type="button"
              >
                <Brain size={14} />
                <span> Consigli AI</span>
              </button>
            </div>

            <div className="team-card-clean__progress">
              <div className="progress-bar">
                <i style={{ width: `${usedPercent}%` }} />
              </div>
            </div>

            <div className="team-card-clean__roles">
              {rolesMode === 'mantra'
                ? buckets.map((role) => {
                  const rolePlayers = roster.filter((p) => getMantraBucket(getRoleText(p, rolesMode)) === role);

                  return (
                    <div key={role} className="role-column-clean">
                      <div className={`role-column-clean__header role-column-clean__header--${role}`}>
                        <RoleHeader role={role} />
                      </div>

                      <div className="role-column-clean__list">
                        {rolePlayers.map((p) => (
                          <div key={p.id} className="player-row-clean">
                            <div className="player-row-clean__left player-row-clean__left--mantra">
                              <TeamBadge team={p.playerSnapshot?.team || p.playerTeam} size={30} />

                              <span className="player-row-clean__name">
                                {p.playerSnapshot?.name || p.playerName}
                              </span>

                              <div
                                className="player-row-clean__roles-inline"
                                aria-label={`Ruoli: ${getRoleText(p, rolesMode)}`}
                              >
                                {splitRoleTokens(getRoleText(p, rolesMode)).map((r) => (
                                  <MantraRoleChip key={`${p.id}-${r}`} role={r} />
                                ))}
                              </div>
                            </div>

                            <div className="player-row-clean__right">
                              <span className="player-row-clean__price">{p.credits} cr</span>

                              <Tooltip
                                as="button"
                                className="player-row-clean__detail"
                                type="button"
                                onClick={() => onOpenPlayerDetail(p.playerId)}
                                content="Apri dettaglio"
                              >
                                <ExternalLink size={14} />
                              </Tooltip>

                              {!readOnly && (
                                <Tooltip
                                  as="button"
                                  className="player-row-clean__delete"
                                  type="button"
                                  onClick={() => onRemovePurchase(p.playerId)}
                                  content="Rimuovi giocatore"
                                >
                                  <Trash2 size={14} />
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        ))}

                        {!rolePlayers.length && <div className="empty">—</div>}
                      </div>

                      <div className="role-column-clean__footer">
                        <div className="role-column-clean__footerItem role-column-clean__footerItem--spent">
                          <span>Spesa ruolo</span>
                          <strong>{stats.spentByRole?.[role] || 0} cr</strong>
                        </div>

                        <div className="role-column-clean__footerItem role-column-clean__footerItem--remaining">
                          <span>Giocatori residui</span>
                          <strong>{stats.remainingByRole?.[role] ?? 0}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })
                : ROLE_ORDER.map((role) => {
                  const rolePlayers = roster.filter((p) => (p.playerSnapshot?.role || p.role) === role);

                  const purchasedByRole = stats?.purchasedByRole?.[role] || rolePlayers.length || 0;
                  const maxByRole = rosterLimits?.[role] ?? 0;
                  const remainingSlots = Math.max(
                    stats?.remainingByRole?.[role] ?? (maxByRole - purchasedByRole),
                    0
                  );

                  const spentByRole = stats?.spentByRole?.[role] || 0;

                  return (
                    <div key={role} className="role-column-clean">
                      <div className={`role-column-clean__header role-column-clean__header--${role}`}>
                        <RoleHeader role={role} />
                      </div>

                      <div className="role-column-clean__list">
                        {rolePlayers.map((p) => (
                          <div key={p.id} className="player-row-clean">
                            <div className="player-row-clean__left player-row-clean__left--classic">
                              <TeamBadge team={p.playerSnapshot?.team || p.playerTeam} size={30} />
                              <span className="player-row-clean__name">
                                {p.playerSnapshot?.name || p.playerName}
                              </span>
                            </div>

                            <div className="player-row-clean__right">
                              <span className="player-row-clean__price">{p.credits} cr</span>

                              <Tooltip
                                as="button"
                                className="player-row-clean__detail"
                                type="button"
                                onClick={() => onOpenPlayerDetail(p.playerId)}
                                content="Apri dettaglio"
                              >
                                <ExternalLink size={14} />
                              </Tooltip>

                              {!readOnly && (
                                <Tooltip
                                  as="button"
                                  className="player-row-clean__delete"
                                  type="button"
                                  onClick={() => onRemovePurchase(p.playerId)}
                                  content="Rimuovi giocatore"
                                >
                                  <Trash2 size={14} />
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        ))}

                        {!rolePlayers.length && <div className="empty">—</div>}
                      </div>

                      <div className="role-column-clean__footer">
                        <div className="role-column-clean__footerItem role-column-clean__footerItem--spent">
                          <span>Spesa ruolo</span>
                          <strong>{spentByRole} cr</strong>
                        </div>

                        <div className="role-column-clean__footerItem role-column-clean__footerItem--remaining">
                          <span>Giocatori residui</span>
                          <strong>{remainingSlots}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </article>
        );
      })}

      {aiOverlayTeam !== null && (
        <AISuggestionsOverlay
          players={players}
          purchases={allPurchases}
          myRosterPurchases={(rosterByTeam[aiOverlayTeam] || []).filter((p) => !p?.deleted)}
          auction={auction}
          teamStats={teamEconomy[aiOverlayTeam]}
          totalTeams={teamNames.length}
          teamName={aiOverlayTeam}
          rolesMode={rolesMode}
          onPurchase={onPurchase}
          readOnly={readOnly}
          onClose={() => setAiOverlayTeam(null)}
          playProbabilityById={playProbabilityById}
        />
      )}

      {compactOverlayOpen && (
        <CompactViewOverlay
          teamNames={teamNames}
          rosterByTeam={rosterByTeam}
          teamEconomy={teamEconomy}
          rolesMode={rolesMode}
          onClose={() => setCompactOverlayOpen(false)}
          marketSelection={marketSelection}
        />
      )}

      {removedOverlayOpen && (
        <RemovedPlayersOverlay
          purchases={allPurchases}
          onRestorePurchase={onRestorePurchase}
          onClose={() => setRemovedOverlayOpen(false)}
          readOnly={readOnly}
        />
      )}

      {legheResolution && (
        <LegheResolutionOverlay
          key={legheResolution.resetKey}
          items={legheResolution.items}
          onCancel={() => setLegheResolution(null)}
          onConfirm={handleConfirmLegheResolution}
          onReset={handleResetLegheOverrides}
        />
      )}

      {legheUnmatched && (
        <div className="overlay-backdrop" onClick={() => setLegheUnmatched(null)}>
          <div className="overlay-panel glass leghe-unmatched-panel" onClick={(e) => e.stopPropagation()}>
            <div className="overlay-head">
              <div className="overlay-head__title">
                <AlertTriangle size={22} color="#f59e0b" />
                <div>
                  <span>Esporta per Leghe</span>
                  <h3>{legheUnmatched.length} giocatori esclusi dall'export</h3>
                </div>
              </div>

              <button className="overlay-close" type="button" onClick={() => setLegheUnmatched(null)}>
                <X size={18} />
              </button>
            </div>

            <p className="leghe-unmatched-hint">
              Il file è stato scaricato, ma questi giocatori non sono nel CSV: o li hai esclusi tu
              manualmente, o non hanno una corrispondenza nel listone Fantacalcio.it 2025/26 (giovani
              o riserve non quotati). Se servono in Leghe Fantacalcio, vanno aggiunti a mano.
            </p>

            <ul className="leghe-unmatched-list">
              {legheUnmatched.map((item, idx) => (
                <li key={`${item.managerTeam}-${item.playerName}-${idx}`}>
                  <strong>{item.playerName || 'Giocatore sconosciuto'}</strong>
                  <span>{item.playerTeam}</span>
                  <span className="leghe-unmatched-list__team">{item.managerTeam}</span>
                </li>
              ))}
            </ul>

            <div className="leghe-unmatched-footer">
              <button type="button" className="leghe-resolve-reset" onClick={handleResetLegheOverrides}>
                <RotateCcw size={13} />
                Cancella scelte salvate
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}